import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/auth/request-password-reset", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-16 bg-bg">
      <div className="w-full max-w-md bg-white border border-line rounded-lg shadow-card p-8" data-testid="forgot-password-page">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand/10 text-brand rounded-full text-[11px] font-display uppercase tracking-[0.2em]">
          <KeyRound className="w-3 h-3" /> Recupero password
        </div>
        {sent ? (
          <>
            <h1 className="mt-4 font-display text-3xl text-ink">Email inviata</h1>
            <div className="mt-6 p-4 bg-brand/[0.06] border border-brand/30 rounded-md flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-brand flex-shrink-0" />
              <div className="text-sm text-ink leading-relaxed">
                Se <strong>{email}</strong> è associata a un account, riceverai un&apos;email con il link per reimpostare la password.
                Controlla anche nella cartella <strong>Spam</strong>. Il link è valido per <strong>60 minuti</strong>.
              </div>
            </div>
            <Link
              to="/login"
              data-testid="back-to-login"
              className="mt-6 inline-flex items-center gap-2 text-sm text-brand hover:underline"
            >
              <ArrowLeft className="w-3 h-3" /> Torna al login
            </Link>
          </>
        ) : (
          <>
            <h1 className="mt-4 font-display text-3xl text-ink">Password dimenticata?</h1>
            <p className="mt-2 text-sm text-ink-muted">
              Inserisci l&apos;email del tuo account. Ti manderemo un link per creare una nuova password.
            </p>
            <form onSubmit={submit} className="mt-6">
              <label className="block mb-4">
                <span className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">Email aziendale</span>
                <div className="relative">
                  <Mail className="w-4 h-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input"
                    placeholder="nome@azienda.it"
                    className="w-full h-11 pl-10 pr-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
                  />
                </div>
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit"
                className="w-full h-11 bg-brand text-white font-display uppercase tracking-widest text-sm rounded-md hover:bg-brand-dark hover:shadow-brand-glow disabled:opacity-60"
              >
                {loading ? "Invio in corso…" : "Invia link di reset"}
              </button>
            </form>
            <p className="mt-6 text-sm text-ink-muted text-center">
              <Link to="/login" className="text-brand hover:underline inline-flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Torna al login
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
